import AddIcon from "@material-ui/icons/Add";
import React from "react";
import { connect, FieldValidator, FormikContextType, getIn } from "formik";
import styled from "styled-components";
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from "@material-ui/core";
import cuideoTheme from "../../containers/themes/defaultTheme";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";

const StyledFormControl = styled(FormControl)<{
  size?: string;
}>`
  .MuiTypography-body1 {
    font-size: 1rem;
    color: ${cuideoTheme.palette.primary.light};
  }

  ${({ error }) => {
    if (error) {
      return `
        .MuiTypography-body1 {
          color: ${cuideoTheme.palette.error.main};
        }
      `;
    }
  }}

  ${({ size }) => {
    if (size === "small") {
      return `
        .MuiTypography-body1 {
          font-size: 0.8rem;
          padding: 0.2rem 0 0.2rem 0;
        }
      `;
    }
  }}
`;

const StyledLabel = styled.label<{ error?: any }>`
  font-family: "Source Sans Pro", Helvetica, Arial, sans-serif;
  color: ${(props) => props.error && "#ff0000"};
  line-height: 1.5;
  margin-bottom: 0.25rem;
`;

const StyledButton = styled(Button)``;

const StyledDialog = styled(Dialog)``;

const StyledDialogButton = styled(Button)``;

const StyledFormControlLabel = styled(FormControlLabel)`
  .MuiSvgIcon-root {
    height: 1.25rem;
    width: 1.25rem;
  }
  .MuiTypography-body1 {
    font-size: 0.875rem;
    line-height: 1.1;
  }
`;

const StyledChip = styled(Chip)`
  margin: 0 0.125rem 0.125rem 0;
  font-size: 0.875rem;
  border-radius: 0.25rem;
  font-weight: 600;
  background-color: ${cuideoTheme.palette.primary.light};
`;

const StyledInputWrapper = styled.div`
  margin-bottom: 1rem;
`;

export const StyledOutlinedInput = styled(OutlinedInput)`
  background-color: rgba(228, 235, 239, 0.5);
  height: 2.25rem;
  font-size: 1rem;
  border-radius: 0.325rem;
  padding-left: 0.375rem;
  padding-right: 0.375rem;
  transition: background-color 0.125s ease-in-out;

  .MuiSvgIcon-root {
    width: 1.325rem;
    height: 1.325rem;
    transition: opacity 0.125s ease-in-out;
    opacity: 0.5;
  }

  .MuiOutlinedInput-notchedOutline {
    opacity: 0;
  }

  &:hover {
    background-color: rgba(228, 235, 239, 0.75);
  }

  &.Mui-focused {
    background-color: rgba(228, 235, 239, 0.05);

    .MuiSvgIcon-root {
      opacity: 1;
    }

    .MuiOutlinedInput-notchedOutline {
      opacity: 1;
    }
  }
`;

interface IProps<Values = {}> {
  name: string;
  required?: boolean;
  variant?: "standard" | "outlined" | "filled" | "compact";
  size?: "medium" | "small";
  margin?: "nomargin" | "normal";
  label?: React.ReactNode;
  labelSuffix?: React.ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
  options: { value: string; label: string }[];
  validate?: FieldValidator;
  forceComponentUpdate?: boolean;
  formik: FormikContextType<Values>;
  messages?: {
    addLabel?: string;
    modifyLabel?: string;
    dialogTitle?: string;
    dialogCancelButton?: string;
    dialogAcceptButton?: string;
    searchPlaceHolder?: string;
  };
}

interface IState {
  modalOpen: boolean;
  searchTerm: string;
  value: { value: string; label: string }[];
}

class FormikModalCheckboxes extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      modalOpen: false,
      searchTerm: "",
      value: [],
    };

    this.handleOpenModal = this.handleOpenModal.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.handleAccept = this.handleAccept.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleSearchReset = this.handleSearchReset.bind(this);
  }

  shouldComponentUpdate(nextProps: IProps, nextState: IState) {
    if (nextProps.forceComponentUpdate) {
      return true;
    }
    const next = nextProps.formik;
    const cur = this.props.formik;
    const name = this.props.name;

    if (this.props.options !== nextProps.options) {
      return true;
    }
    if (
      this.state.modalOpen !== nextState.modalOpen ||
      this.state.value !== nextState.value ||
      this.state.searchTerm !== nextState.searchTerm
    ) {
      return true;
    }

    if (
      getIn(next.values, name) !== getIn(cur.values, name) ||
      getIn(next.errors, name) !== getIn(cur.errors, name) ||
      getIn(next.touched, name) !== getIn(cur.touched, name)
    ) {
      return true;
    }
    return false;
  }

  componentDidMount() {
    this.props.formik.registerField(this.props.name, {
      validate: this.props.validate,
    });
  }

  componentDidUpdate(prevProps: any) {
    if (this.props.name !== prevProps.name) {
      this.props.formik.unregisterField(prevProps.name);
      this.props.formik.registerField(this.props.name, {
        validate: this.props.validate,
      });
    }

    if (this.props.validate !== prevProps.validate) {
      this.props.formik.registerField(this.props.name, {
        validate: this.props.validate,
      });
    }
  }

  componentWillUnmount() {
    this.props.formik.unregisterField(this.props.name);
  }

  handleOpenModal() {
    this.setState({
      modalOpen: true,
      value: (this.props.formik.values as any)[this.props.name],
    });
  }

  handleCloseModal() {
    this.setState({ modalOpen: false });
  }

  handleCheckboxChange(value: string, label: string) {
    const found = this.state.value.find((v) => v.value === value);
    if (found) {
      this.setState({
        value: this.state.value.filter((v) => v.value !== value),
      });
    } else {
      this.setState({
        value: [...this.state.value, { value, label }],
      });
    }
  }

  handleAccept() {
    this.props.formik.setFieldValue(this.props.name, this.state.value);
    this.props.formik.setFieldTouched(this.props.name, true, false);
    this.setState({ modalOpen: false });
  }

  handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ searchTerm: e.target.value });
  }

  handleSearchReset() {
    this.setState({ searchTerm: "" });
  }

  render() {
    const { fullWidth, size, label, name, required, formik, options } =
      this.props;

    const messages = this.props.messages || {};
    const addLabel = messages.addLabel || "Add";
    const dialogTitle = messages.dialogTitle || "Modify selection";
    const dialogCancelButton = messages.dialogCancelButton || "Cancel";
    const dialogAcceptButton = messages.dialogAcceptButton || "Accept";
    const searchPlaceholder = messages.searchPlaceHolder || "Buscar...";

    const error = getIn(formik.errors, name);
    const touched = !!getIn(formik.touched, name);

    const unchangedValues = getIn(formik.values, name);

    const filteredOptions = options.filter((option) => {
      if (this.state.searchTerm === "") return true;

      const isChecked = this.state.value.find(
        (item) => item.value === option.value
      );
      if (isChecked) return true;

      if (
        option.label.toLowerCase().includes(this.state.searchTerm.toLowerCase())
      ) {
        return true;
      }

      return false;
    });

    return (
      <StyledFormControl
        fullWidth={fullWidth}
        size={size || "small"}
        error={touched && !!error}
      >
        <StyledLabel
          error={touched && !!error}
          htmlFor={name}
          className="MuiTypography-root MuiTypography-body1"
        >
          {label} {required && " *"}
        </StyledLabel>
        <Grid
          container
          spacing={8}
          alignItems="flex-start"
          justifyContent="space-between"
          wrap="nowrap"
        >
          <Grid item>
            {unchangedValues.map((v: { value: string; label: string }) => (
              <StyledChip
                key={`${name}__${v.value}`}
                label={v.label}
                color="secondary"
              />
            ))}
            {touched && error && (
              <FormHelperText>
                {error.value ? error.value : error}
              </FormHelperText>
            )}
          </Grid>
          <Grid item>
            <StyledButton
              onClick={this.handleOpenModal}
              size="small"
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
            >
              {addLabel}
            </StyledButton>
          </Grid>
        </Grid>
        <StyledDialog open={this.state.modalOpen}>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogContent>
            <StyledInputWrapper>
              <StyledOutlinedInput
                placeholder={searchPlaceholder}
                onChange={this.handleSearchChange}
                value={this.state.searchTerm}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                }
                fullWidth
                endAdornment={
                  this.state.searchTerm !== "" ? (
                    <InputAdornment position="end">
                      <IconButton onClick={this.handleSearchReset}>
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined
                }
              />
            </StyledInputWrapper>
            <Grid container spacing={8}>
              {filteredOptions.map((option) => {
                const isChecked = this.state.value.find(
                  (item) => item.value === option.value
                );
                return (
                  <Grid key={`fmc_op_${option.value}`} item xs={6} md={4}>
                    <FormControl fullWidth>
                      <StyledFormControlLabel
                        label={option.label}
                        control={
                          <Checkbox
                            color="primary"
                            checked={!!isChecked}
                            onChange={() => {
                              this.handleCheckboxChange(
                                option.value,
                                option.label
                              );
                            }}
                          />
                        }
                        checked={!!isChecked}
                      />
                    </FormControl>
                  </Grid>
                );
              })}
            </Grid>
          </DialogContent>
          <DialogActions>
            <StyledDialogButton color="primary" onClick={this.handleCloseModal}>
              {dialogCancelButton}
            </StyledDialogButton>
            <StyledDialogButton
              color="primary"
              variant="contained"
              onClick={this.handleAccept}
            >
              {dialogAcceptButton}
            </StyledDialogButton>
          </DialogActions>
        </StyledDialog>
      </StyledFormControl>
    );
  }
}

export default connect<Omit<IProps, "formik">, IState>(
  FormikModalCheckboxes as any
);
